import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Assessment as ReportIcon,
  PictureAsPdf as PDFIcon,
  TableChart as ExcelIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import {
  clraReportsAPI,
  statutoryReportsAPI,
  analyticsReportsAPI,
} from "../services/api";
import { downloadExcelFile, downloadPDFFile } from "../utils/fileDownload";

const Reports = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [selectedState, setSelectedState] = useState("MH");
  const [generating, setGenerating] = useState(false);

  // Separate counts for each report type
  const [reportCounts, setReportCounts] = useState({
    clra: 0,
    statutory: 0,
    analytics: 0,
  });

  const tabs = [
    { label: "CLRA Reports", value: 0, api: clraReportsAPI },
    { label: "Statutory Reports", value: 1, api: statutoryReportsAPI },
    { label: "Analytics Reports", value: 2, api: analyticsReportsAPI },
  ];

  const states = [
    { code: "MH", name: "Maharashtra" },
    { code: "KA", name: "Karnataka" },
    { code: "TN", name: "Tamil Nadu" },
    { code: "GJ", name: "Gujarat" },
    { code: "DL", name: "Delhi" },
    { code: "UP", name: "Uttar Pradesh" },
  ];

  const months = [
    { value: 1, name: "January" },
    { value: 2, name: "February" },
    { value: 3, name: "March" },
    { value: 4, name: "April" },
    { value: 5, name: "May" },
    { value: 6, name: "June" },
    { value: 7, name: "July" },
    { value: 8, name: "August" },
    { value: 9, name: "September" },
    { value: 10, name: "October" },
    { value: 11, name: "November" },
    { value: 12, name: "December" },
  ];

  const currentAPI = tabs[tabValue].api;

  // Set tab based on route
  useEffect(() => {
    if (location.pathname === "/statutory-reports") {
      setTabValue(1); // Statutory Reports tab
    } else if (location.pathname === "/analytics-reports") {
      setTabValue(2); // Analytics Reports tab
    } else if (location.pathname === "/clra-reports") {
      setTabValue(0); // CLRA Reports tab
    } else {
      setTabValue(0); // CLRA Reports tab (default)
    }
  }, [location.pathname]);

  const loadReportTypes = useCallback(async () => {
    try {
      const response = await currentAPI.getTypes();
      setReportTypes(response.data.data || []);
    } catch (error) {
      console.error("Error loading report types:", error);
      enqueueSnackbar("Failed to load report types", { variant: "error" });
    }
  }, [currentAPI, enqueueSnackbar]);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await currentAPI.getAll({
        page: 1,
        limit: 50,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      const reportsData = response.data.data?.reports || [];
      console.log("=== REPORTS API DEBUG ===");
      console.log("Current logged-in user:", user);
      console.log("Full API response:", response.data);
      console.log("Reports data:", reportsData);
      if (reportsData.length > 0) {
        console.log(
          "First report full object:",
          JSON.stringify(reportsData[0], null, 2)
        );
        console.log("GeneratedBy field:", reportsData[0].generatedBy);
        console.log("User field:", reportsData[0].user);
        console.log("Metadata field:", reportsData[0].metadata);
        console.log(
          "Metadata.generatedBy field:",
          reportsData[0].metadata?.generatedBy
        );
        console.log("All keys in first report:", Object.keys(reportsData[0]));
      }
      console.log("=== END DEBUG ===");
      setReports(reportsData);
    } catch (error) {
      console.error("Error loading reports:", error);
      console.error("Error details:", error.response?.data);
      enqueueSnackbar(
        `Failed to load reports: ${
          error.response?.data?.error || error.message
        }`,
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  }, [currentAPI, enqueueSnackbar, user]);

  // Load counts for all report types
  const loadAllReportCounts = useCallback(async () => {
    try {
      const [clraResponse, statutoryResponse, analyticsResponse] =
        await Promise.all([
          clraReportsAPI.getAll({ page: 1, limit: 1 }),
          statutoryReportsAPI.getAll({ page: 1, limit: 1 }),
          analyticsReportsAPI.getAll({ page: 1, limit: 1 }),
        ]);

      setReportCounts({
        clra: clraResponse.data.data?.total || 0,
        statutory: statutoryResponse.data.data?.total || 0,
        analytics: analyticsResponse.data.data?.total || 0,
      });
    } catch (error) {
      console.error("Error loading report counts:", error);
    }
  }, []);

  useEffect(() => {
    loadReportTypes();
    loadReports();
    loadAllReportCounts();
  }, [tabValue, loadReportTypes, loadReports, loadAllReportCounts]);

  const handleGenerateReport = async () => {
    if (!selectedReportType) {
      enqueueSnackbar("Please select a report type", { variant: "warning" });
      return;
    }

    try {
      setGenerating(true);
      const payload = { period: selectedPeriod };

      // Add state for statutory reports
      if (tabValue === 1) {
        payload.state = selectedState;
      }

      await currentAPI.generate(selectedReportType, payload);

      enqueueSnackbar("Report generated successfully", { variant: "success" });
      setOpenGenerateDialog(false);
      loadReports();
      loadAllReportCounts(); // Update counts after generating
    } catch (error) {
      console.error("Error generating report:", error);
      console.error("Error details:", error.response?.data);
      enqueueSnackbar(
        `Failed to generate report: ${
          error.response?.data?.error || error.message
        }`,
        { variant: "error" }
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadExcel = async (reportId) => {
    try {
      const response = await currentAPI.downloadExcel(reportId);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      downloadExcelFile(blob, reportId);
      enqueueSnackbar("Excel file downloaded successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error downloading Excel:", error);
      enqueueSnackbar("Failed to download Excel file", { variant: "error" });
    }
  };

  const handleDownloadPDF = async (reportId) => {
    try {
      const response = await currentAPI.downloadPDF(reportId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      downloadPDFFile(blob, reportId);
      enqueueSnackbar("PDF file downloaded successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error downloading PDF:", error);
      enqueueSnackbar("Failed to download PDF file", { variant: "error" });
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      try {
        await currentAPI.delete(reportId);
        enqueueSnackbar("Report deleted successfully", { variant: "success" });
        loadReports();
        loadAllReportCounts(); // Update counts after deleting
      } catch (error) {
        console.error("Error deleting report:", error);
        enqueueSnackbar("Failed to delete report", { variant: "error" });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "warning";
      case "generated":
        return "success";
      case "submitted":
        return "info";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getReportTypeName = (type) => {
    const reportType = reportTypes.find((rt) => rt.type === type);
    return reportType ? reportType.name : type;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Reports Management</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadReports();
                loadAllReportCounts();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenGenerateDialog(true)}
            >
              Generate Report
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
          >
            {tabs.map((tab) => {
              const count =
                tab.value === 0
                  ? reportCounts.clra
                  : tab.value === 1
                  ? reportCounts.statutory
                  : reportCounts.analytics;

              return (
                <Tab
                  key={tab.value}
                  label={
                    <Badge
                      badgeContent={count}
                      color="primary"
                      sx={{ "& .MuiBadge-badge": { right: -8, top: 8 } }}
                    >
                      {tab.label}
                    </Badge>
                  }
                />
              );
            })}
          </Tabs>
        </Card>

        {/* Reports Table */}
        <Card>
          <CardContent>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Report Type</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Generated By</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <ReportIcon sx={{ mr: 1, color: "primary.main" }} />
                          <Box>
                            <Typography variant="subtitle2">
                              {getReportTypeName(report.reportType)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {report.reportType}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.period?.month && report.period?.year
                            ? `${months[report.period.month - 1]?.name} ${
                                report.period.year
                              }`
                            : report.period?.startDate && report.period?.endDate
                            ? `${dayjs(report.period.startDate).format(
                                "DD/MM/YYYY"
                              )} - ${dayjs(report.period.endDate).format(
                                "DD/MM/YYYY"
                              )}`
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {report.metadata?.generatedBy?.firstName &&
                          report.metadata?.generatedBy?.lastName
                            ? `${report.metadata.generatedBy.firstName} ${report.metadata.generatedBy.lastName}`
                            : report.metadata?.generatedBy?.name
                            ? report.metadata.generatedBy.name
                            : report.metadata?.generatedBy?.email
                            ? report.metadata.generatedBy.email
                            : report.generatedBy?.firstName &&
                              report.generatedBy?.lastName
                            ? `${report.generatedBy.firstName} ${report.generatedBy.lastName}`
                            : report.generatedBy?.name
                            ? report.generatedBy.name
                            : report.generatedBy?.email
                            ? report.generatedBy.email
                            : report.user?.firstName && report.user?.lastName
                            ? `${report.user.firstName} ${report.user.lastName}`
                            : report.user?.name
                            ? report.user.name
                            : report.user?.email
                            ? report.user.email
                            : user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`
                            : user?.name
                            ? user.name
                            : user?.email
                            ? user.email
                            : "System"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.metadata?.generatedBy?.email ||
                            report.generatedBy?.email ||
                            report.user?.email ||
                            user?.email ||
                            ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(report.createdAt).format("DD/MM/YYYY HH:mm")}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download Excel">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadExcel(report._id)}
                            color="success"
                          >
                            <ExcelIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadPDF(report._id)}
                            color="error"
                          >
                            <PDFIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Report">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteReport(report._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {reports.length === 0 && !loading && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No reports found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate your first report to get started
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Generate Report Dialog */}
        <Dialog
          open={openGenerateDialog}
          onClose={() => setOpenGenerateDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Generate New Report</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={selectedReportType}
                  onChange={(e) => setSelectedReportType(e.target.value)}
                  label="Report Type"
                >
                  {reportTypes.map((type) => (
                    <MenuItem key={type.type} value={type.type}>
                      <Box>
                        <Typography variant="body2">{type.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedPeriod.month}
                      onChange={(e) =>
                        setSelectedPeriod({
                          ...selectedPeriod,
                          month: e.target.value,
                        })
                      }
                      label="Month"
                    >
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Year"
                    type="number"
                    value={selectedPeriod.year}
                    onChange={(e) =>
                      setSelectedPeriod({
                        ...selectedPeriod,
                        year: parseInt(e.target.value),
                      })
                    }
                  />
                </Grid>
              </Grid>

              {/* State selection for statutory reports */}
              {tabValue === 1 && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={selectedState}
                    onChange={(e) => setSelectedState(e.target.value)}
                    label="State"
                  >
                    {states.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Date range for analytics reports */}
              {tabValue === 2 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="Start Date"
                      value={dayjs(selectedPeriod.startDate)}
                      onChange={(newValue) =>
                        setSelectedPeriod({
                          ...selectedPeriod,
                          startDate: newValue?.format("YYYY-MM-DD"),
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label="End Date"
                      value={dayjs(selectedPeriod.endDate)}
                      onChange={(newValue) =>
                        setSelectedPeriod({
                          ...selectedPeriod,
                          endDate: newValue?.format("YYYY-MM-DD"),
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenGenerateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleGenerateReport}
              variant="contained"
              disabled={generating || !selectedReportType}
            >
              {generating ? "Generating..." : "Generate Report"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
